import {
  Tr,
  Td,
  Switch,
  Select,
  Text,
  IconButton,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverFooter,
  PopoverArrow,
  PopoverCloseButton,
  Button,
  useToast,
  Flex
} from '@chakra-ui/react'
import { FC, useEffect, useState } from 'react'
import Highlighter from 'react-highlight-words'
import { AiOutlineDelete } from 'react-icons/ai'
import { FiEdit } from 'react-icons/fi'

import useFilters from '@hook/shared/use-filters'

import { podeVincularUsuarioSap } from '@component/Modules/Users/UsuarioSapSelect'

import user from '@service/user'

import type { IProfileFilter } from '@base/@types/providers/Shared/FiltersProvider'

interface IUsersModalTableTrProps {
  data: { [key: string]: any }
  profiles: IProfileFilter[]
  loadingProfiles: boolean
  onSelect: (data: any) => void
  onUpdate: () => void
}

const UsersModalTableTr: FC<IUsersModalTableTrProps> = ({
  data,
  profiles,
  loadingProfiles = false,
  onSelect = () => {},
  onUpdate = () => {}
}) => {
  const { usersQuery: fastQuery } = useFilters()

  const [profileId, setProfileId] = useState(data.idPerfil)
  const [switcher, setSwitcher] = useState<boolean>(data.revisarStatus)
  const [loading, setLoading] = useState(false)

  const toast = useToast()

  useEffect(() => {
    setProfileId(data.idPerfil)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const handleProfileChange = async (e: any) => {
    const oldProfile = profileId

    setProfileId(e.target.value)

    try {
      await user.update(
        {
          ...data,
          idPerfil: Number(e.target.value),
          // PUT substitui o cadastro: mantém o vínculo SAP só se o novo perfil permitir
          usuarioSap: podeVincularUsuarioSap(e.target.value)
            ? data.usuarioSap ?? null
            : null
        },
        data?.email
      )

      toast({
        title: 'Sucesso!',
        description: 'Perfil do usuário atualizado com sucesso.',
        position: 'top',
        status: 'success'
      })

      onUpdate()
    } catch {
      setProfileId(oldProfile)
      toast({
        title: 'Oopss...',
        description: 'Não foi possível trocar o perfil do usuário.',
        position: 'top',
        status: 'error'
      })
    }
  }

  const handleSwitcherChange = async () => {
    const oldSwitch = switcher

    setSwitcher(old => !old)

    try {
      await user.update(
        {
          ...data,
          revisarStatus: !switcher,
          usuarioSap: data.usuarioSap ?? null
        },
        data?.email
      )

      toast({
        title: 'Sucesso!',
        description: 'Configuração de revisão de status atualizada.',
        position: 'top',
        status: 'success'
      })

      onUpdate()
    } catch {
      setSwitcher(oldSwitch)

      toast({
        title: 'Oopss...',
        description: 'Não foi possível efetuar a alteração.',
        position: 'top',
        status: 'error'
      })
    }
  }

  const handleDestroy = async () => {
    setLoading(true)

    try {
      await user.destroy(data.id, data?.email)

      onUpdate()
    } catch {
      setLoading(false)
      toast({
        title: 'Oopss...',
        description: 'Não foi possível remover o usuário.',
        position: 'top',
        status: 'error'
      })
    }

    setLoading(false)
  }

  return (
    <Tr>
      <Td>{data.id}</Td>
      <Td>
        {fastQuery.length > 0 ? (
          <Text maxW="220px" isTruncated>
            <Highlighter
              searchWords={fastQuery.split(' ')}
              textToHighlight={String(data.nome || 'Não informado')}
            />
          </Text>
        ) : (
          <Text maxW="200px" isTruncated>
            {data.nome || 'Não informado'}
          </Text>
        )}
      </Td>
      <Td>
        {fastQuery.length > 0 ? (
          <Text maxW="220px" isTruncated>
            <Highlighter
              searchWords={fastQuery.split(' ')}
              textToHighlight={String(data.email || 'Não informado')}
            />
          </Text>
        ) : (
          <Text maxW="220px" isTruncated>
            {data.email || 'Não cadastrado'}
          </Text>
        )}
      </Td>
      <Td>
        <Select
          size="sm"
          colorScheme="white"
          w="100%"
          value={profileId}
          onChange={handleProfileChange}
          isDisabled={loadingProfiles}
          placeholder={loadingProfiles ? 'Carregando perfis...' : ''}
        >
          {profiles.map((profile: any) => (
            <option
              style={{ background: 'white' }}
              key={`profile-option-${profile.value}`}
              value={profile.value}
            >
              {profile.label}
            </option>
          ))}
        </Select>
      </Td>
      <Td isNumeric>
        <Flex justifyContent="center">
          <Switch
            marginTop={1}
            isChecked={switcher}
            onChange={handleSwitcherChange}
            css={{
              '.css-l1lhmt': {
                background: switcher ? 'cean' : 'lightGray',
                border: switcher ? '1px solid #6CAFE8' : '1px solid #BDBDBD'
              },
              '.chakra-switch__thumb': {
                boxShadow: '0 0 2px #000000'
              }
            }}
          />
        </Flex>
      </Td>
      <Td isNumeric style={{ display: 'flex' }}>
        <IconButton
          aria-label=""
          size="sm"
          icon={<FiEdit />}
          onClick={() => onSelect(data)}
          mr={2}
        />
        <Popover placement="left-start">
          <PopoverTrigger>
            <IconButton
              aria-label=""
              colorScheme="red"
              size="sm"
              icon={<AiOutlineDelete />}
            />
          </PopoverTrigger>
          <PopoverContent textAlign="left" bg="white">
            <PopoverArrow bgColor="white" />
            <PopoverCloseButton />
            <PopoverHeader fontWeight="semibold">Confirmação!</PopoverHeader>
            <PopoverBody py={4}>
              Tem certeza que deseja excluir o{' '}
              {data.nome ? data.nome.split(' ')[0] : data.nome}?
            </PopoverBody>
            <PopoverFooter textAlign="right">
              <Button
                size="sm"
                variant="ghost"
                color="red.400"
                isLoading={loading}
                onClick={handleDestroy}
              >
                Sim, excluir agora!
              </Button>
            </PopoverFooter>
          </PopoverContent>
        </Popover>
      </Td>
    </Tr>
  )
}

export default UsersModalTableTr
